import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Chip,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  Security as RoleIcon
} from '@mui/icons-material';

const MembersList = ({
  members,
  roles,
  onAssignRole,
  onRevokeRole,
  onGetUserPermissions,
  canModify
}) => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleMenuOpen = (event, member) => {
    setSelectedMember(member);
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleAssignRoleClick = () => {
    handleMenuClose();
    setAssignDialogOpen(true);
  };

  const handleViewPermissions = async () => {
    handleMenuClose();
    setLoading(true);
    setError(null);
    try {
      const permissions = await onGetUserPermissions(selectedMember.userId);
      setUserPermissions(permissions);
      setPermissionsDialogOpen(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async (roleId) => {
    setLoading(true);
    setError(null);
    try {
      await onAssignRole(selectedMember.userId, roleId);
      setAssignDialogOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeRole = async (roleId) => {
    if (!selectedMember) return;
    setLoading(true);
    setError(null);
    try {
      await onRevokeRole(selectedMember.userId, roleId);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Group permissions by resource for display (with deduplication)
  const groupPermissionsByResource = (permissions) => {
    const grouped = {};
    const seen = new Set();
    permissions.forEach(perm => {
      if (seen.has(perm)) return; // Skip duplicates
      seen.add(perm);
      const [resource] = perm.split(':');
      if (!grouped[resource]) {
        grouped[resource] = [];
      }
      grouped[resource].push(perm);
    });
    return grouped;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Organization Members
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage member roles and permissions
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <List>
            {members.map((member, index) => (
              <React.Fragment key={member.userId}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemAvatar>
                    <Avatar src={member.avatarUrl}>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {member.name || member.email || 'Unknown User'}
                        {member.role === 'OWNER' && (
                          <Chip label="Owner" size="small" color="primary" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {member.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Joined: {new Date(member.joinedAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={(e) => handleMenuOpen(e, member)}
                      disabled={loading}
                    >
                      <MoreIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
            {members.length === 0 && (
              <ListItem>
                <ListItemText
                  primary="No members found"
                  secondary="This organization has no members"
                />
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>

      {/* Member Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewPermissions}>
          View Permissions
        </MenuItem>
        {canModify && selectedMember?.role !== 'OWNER' && (
          <MenuItem onClick={handleAssignRoleClick}>
            Assign Role
          </MenuItem>
        )}
      </Menu>

      {/* Assign Role Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign Role to {selectedMember?.name || selectedMember?.email}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a role to assign to this member
          </Typography>
          <List>
            {roles.map((role) => (
              <ListItem
                key={role.id}
                button
                onClick={() => handleAssignRole(role.id)}
                disabled={loading}
              >
                <ListItemAvatar>
                  <Avatar>
                    <RoleIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={role.name}
                  secondary={
                    <>
                      {role.description}
                      {role.isSystemRole && (
                        <Chip label="System" size="small" sx={{ ml: 1 }} />
                      )}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* User Permissions Dialog */}
      <Dialog
        open={permissionsDialogOpen}
        onClose={() => setPermissionsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Permissions for {selectedMember?.name || selectedMember?.email}
        </DialogTitle>
        <DialogContent>
          {userPermissions && (
            <Box>
              {/* Roles Section */}
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
                Assigned Roles
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {userPermissions.roles?.length > 0 ? (
                  userPermissions.roles.map((role) => (
                    <Chip
                      key={role.id}
                      label={role.name}
                      color="primary"
                      variant="outlined"
                      onDelete={canModify && !role.isSystemRole ? () => handleRevokeRole(role.id) : undefined}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No roles assigned
                  </Typography>
                )}
              </Box>

              {/* Permissions Section */}
              <Typography variant="subtitle1" gutterBottom>
                Permissions
              </Typography>
              {Object.entries(groupPermissionsByResource(userPermissions.effectivePermissions || [])).map(([resource, perms]) => (
                <Box key={resource} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ textTransform: 'capitalize' }}>
                    {resource}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {perms.map((perm) => (
                      <Chip
                        key={perm}
                        label={perm.split(':')[1]}
                        size="small"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MembersList;
